import React, { useState, useRef, useEffect } from 'react';
import { Copy, UserPlus, LogOut, ArrowLeftRight, Mic } from 'lucide-react';
import toast from 'react-hot-toast';
import IconSettings from '../icons/IconSettings';

/**
 * Campaign-specific settings gear dropdown for the header.
 * Shows different options based on GM vs Player role.
 */
export default function CampaignSettingsDropdown({
  campaignId,
  isGM,
  forcePlayerView,
  onSwitchToGMView,
  onJoinAsPlayer,
  onLeaveCampaign,
  onOpenChronicleSetup,
}) {
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    function handleClick(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const handleCopyInvite = () => {
    const shareableLink = `${window.location.origin}/join-campaign/${campaignId}`;
    navigator.clipboard.writeText(shareableLink).then(() => {
      toast.success('Campaign invite link copied to clipboard!');
    }).catch(() => {
      prompt('Copy this link to invite players:', shareableLink);
    });
    setOpen(false);
  };

  return (
    <div ref={menuRef} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="p-1.5 sm:p-2 text-gray-400 hover:text-red-400 transition-colors duration-200"
        title="Campaign Settings"
      >
        <IconSettings className="w-5 h-5" />
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-48 bg-gradient-to-br from-gray-900 to-black rounded-md shadow-2xl z-50 border-2 border-red-700">
          <div className="py-1">
            {isGM && forcePlayerView && (
              <button
                onClick={() => { setOpen(false); onSwitchToGMView(); }}
                className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-300 hover:bg-red-900 hover:text-white transition-colors duration-200 text-left"
              >
                <ArrowLeftRight className="w-4 h-4" />
                Switch to GM View
              </button>
            )}
            {isGM && !forcePlayerView && (
              <>
                <button
                  onClick={handleCopyInvite}
                  className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-300 hover:bg-red-900 hover:text-white transition-colors duration-200 text-left"
                >
                  <Copy className="w-4 h-4" />
                  Copy Invite Link
                </button>
                <button
                  onClick={() => { setOpen(false); onJoinAsPlayer(); }}
                  className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-300 hover:bg-red-900 hover:text-white transition-colors duration-200 text-left"
                >
                  <UserPlus className="w-4 h-4" />
                  Join as Player
                </button>
                {onOpenChronicleSetup && (
                  <button
                    onClick={() => { setOpen(false); onOpenChronicleSetup(); }}
                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-300 hover:bg-red-900 hover:text-white transition-colors duration-200 text-left"
                  >
                    <Mic className="w-4 h-4" />
                    Campaign Chronicler Setup
                  </button>
                )}
              </>
            )}
            {!isGM && (
              <button
                onClick={() => { setOpen(false); onLeaveCampaign(); }}
                className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-400 hover:bg-red-900 hover:text-white transition-colors duration-200 text-left"
              >
                <LogOut className="w-4 h-4" />
                Leave Campaign
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
